import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';

const HERO_BG = "https://images.pexels.com/photos/5792322/pexels-photo-5792322.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Typing animation component
const TypingText = ({ text, className, color, delay = 0, onComplete, textStyle }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startTyping, setStartTyping] = useState(false);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      setStartTyping(true);
    }, delay);
    return () => clearTimeout(delayTimer);
  }, [delay]);

  useEffect(() => {
    if (!startTyping) return;
    
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(prev => prev + text[currentIndex]);
        setCurrentIndex(prev => prev + 1);
      }, 150); // Speed of typing (150ms per letter)
      return () => clearTimeout(timer);
    } else if (onComplete) {
      onComplete();
    }
  }, [currentIndex, text, startTyping, onComplete]);

  return (
    <span className={`${className} ${color}`} style={textStyle}>
      {displayText}
      {currentIndex < text.length && startTyping && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ duration: 0.5, repeat: Infinity }}
          className="inline-block w-[3px] h-[1em] bg-current ml-1 align-middle"
        />
      )}
    </span>
  );
};

const HeroSection = ({ onShowPopup }) => {
  const { t } = useLanguage();
  const [hanseComplete, setHanseComplete] = useState(false);
  const [showPizza, setShowPizza] = useState(false);
  const [pizzaComplete, setPizzaComplete] = useState(false);

  // When HANSE typing is complete, trigger popup and then show PIZZA
  const handleHanseComplete = () => {
    setHanseComplete(true);
    // Trigger the popup
    if (onShowPopup) {
      onShowPopup();
    }
    // Start showing PIZZA after a short delay (popup will show)
    setTimeout(() => {
      setShowPizza(true);
    }, 500);
  };

  const handlePizzaComplete = () => {
    setPizzaComplete(true);
  };

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Image with Parallax Effect */}
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/80 via-hanse-dark/70 to-hanse-dark/90" />
      </motion.div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Tagline */}
          <motion.p
            className="text-hanse-gold font-accent text-lg sm:text-xl tracking-widest mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {t('hero.tagline')}
          </motion.p>

          {/* Title - Static "Welcome to" */}
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-2">
            {t('hero.title')}
          </h1>
          
          {/* Brand Name with Typing Animation */}
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 min-h-[1.2em]">
            <TypingText 
              text="HANSE" 
              className="inline-block"
              color="text-[#8B1E3C]"
              textStyle={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5), 0 0 20px rgba(139,30,60,0.3)' }}
              delay={800}
              onComplete={handleHanseComplete}
            />
            {hanseComplete && (
              <span className="text-hanse-red"> </span>
            )}
            {showPizza && (
              <TypingText 
                text="PIZZA" 
                className="inline-block"
                color="text-[#1B2A4E]"
                textStyle={{ textShadow: '0 0 20px rgba(255,255,255,1), 0 0 40px rgba(255,255,255,0.8), 2px 2px 4px rgba(255,255,255,0.9)' }}
                delay={0}
                onComplete={handlePizzaComplete}
              />
            )}
          </h1>

          {/* Subtitle */}
          <AnimatePresence>
            {pizzaComplete && (
              <motion.p 
                className="text-white/80 text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {t('hero.subtitle')}
              </motion.p>
            )}
          </AnimatePresence>

          {/* CTA Buttons */}
          <AnimatePresence>
            {pizzaComplete && (
              <motion.div 
                className="flex flex-col sm:flex-row items-center justify-center gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Button
                  onClick={() => window.open('https://www.ubereats.com/de/store/hanse-pizza/cTNFBxdnR9mVVXQv3W28bg', '_blank')}
                  className="bg-hanse-red hover:bg-hanse-red/90 text-white px-8 py-4 rounded-full font-bold text-lg hover-glow transition-all hover:scale-105 flex items-center gap-2"
                  data-testid="hero-order-btn"
                >
                  {t('hero.orderNow')}
                </Button>
                <Button
                  onClick={() => scrollToSection('#speisekarte')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-hanse-navy px-8 py-4 rounded-full font-bold text-lg transition-all flex items-center gap-2"
                  data-testid="hero-menu-btn"
                >
                  <FileText className="w-5 h-5" />
                  {t('hero.viewMenu')}
                </Button>
                <Button
                  onClick={() => window.location.href = 'tel:+4942148535145'}
                  variant="ghost"
                  className="text-white hover:text-hanse-gold px-6 py-4 font-semibold flex items-center gap-2"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5" />
                  {t('hero.callUs')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <AnimatePresence>
        {pizzaComplete && (
          <motion.div
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 hidden md:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, y: [0, 10, 0] }}
            transition={{ delay: 0.5, duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-white/50 rounded-full" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HeroSection;
